// Start-up: preferences, session, school info, top bar, router (SPEC §9).

import { fetchMe, fetchSchool, logout } from "./api/auth";
import { onMutation, onUnauthorized, type ApiResult } from "./api/client";
import type { AuthUser, SchoolInfo } from "./api/types";
import { applyTheme, getLanguage, setLanguage } from "./preferences";
import { currentFullPath, currentPath, navigate, startRouter } from "./router";
import { routes } from "./routes";
import { endSession, getToken, getUser, setSchool, setUser } from "./session";
import { refreshUnreadCount, stopBell } from "./ui/bell";
import { fitTimelinesToScreen } from "./ui/timeline";
import { highlightCurrentNav, renderTopBar, setUpTopBar } from "./ui/topBar";
import { renderForbiddenPage, renderNotFoundPage } from "./pages/errors/errorPages";
import { onSignedIn, renderSignInPage } from "./pages/signIn/signInPage";

async function startApp(): Promise<void> {
  const appRoot: HTMLElement | null = document.getElementById("app");
  if (appRoot === null) {
    throw new Error("index.html must contain #app");
  }

  applyTheme();
  setLanguage(getLanguage());
  const topBar: HTMLDivElement = document.createElement("div");
  const pageContainer: HTMLDivElement = document.createElement("div");
  appRoot.replaceChildren(topBar, pageContainer);
  setUpTopBar(topBar, signOut);
  wireApiEvents();
  // Day-view grids are sized to the window (SPEC §9.3); keep them fitted when it changes.
  window.addEventListener("resize", fitTimelinesToScreen);
  const isSignedIn: boolean = await restoreSession();
  renderTopBar(isSignedIn);
  onSignedIn(handleSignedIn);
  startRouter(pageContainer, {
    routes,
    signIn: renderSignInPage,
    forbidden: renderForbiddenPage,
    notFound: renderNotFoundPage,
  }, highlightCurrentNav);
}

/** Uses a stored token if it still works; loads school info for "today" (SPEC §8.4). */
async function restoreSession(): Promise<boolean> {
  if (getToken() === null) {
    return false;
  }

  const me: ApiResult<AuthUser> = await fetchMe();
  if (me.ok === false) {
    endSession();
    return false;
  }

  setUser(me.data);
  await loadSchool();
  return true;
}

async function loadSchool(): Promise<void> {
  const school: ApiResult<SchoolInfo> = await fetchSchool();
  if (school.ok) {
    setSchool(school.data);
  }
}

function wireApiEvents(): void {
  onUnauthorized((): void => {
    if (currentPath() === "/sign-in") {
      return;
    }

    endSession();
    stopBell();
    renderTopBar(false);
    navigate("/sign-in?return=" + encodeURIComponent(currentFullPath()));
  });
  onMutation((): void => {
    // Sign-in itself is a POST; the bell is only refreshed once someone is signed in.
    if (getUser() !== null) {
      void refreshUnreadCount();
    }
  });
}

function handleSignedIn(returnPath: string): void {
  renderTopBar(true);
  navigate(returnPath);
}

async function signOut(): Promise<void> {
  await logout();
  endSession();
  stopBell();
  renderTopBar(false);
  navigate("/sign-in");
}

void startApp();
